/** Real telephony over the Metafore voice edge — a thin adapter over the SHARED
 *  agent SDK (@ccv2/agent-sdk, adr-039/adr-050). The SDK drives presence
 *  (heartbeat), inbound (poll → accept), AND outbound (dial → dialing →
 *  answered) uniformly; this provider just maps its snapshot to the softphone's
 *  telephony events and forwards commands. One state machine for both directions,
 *  shared with the console agent-desktop. */
import { AgentClient } from "@ccv2/agent-sdk";
import { PollingTransport } from "@ccv2/agent-sdk";
import type { AgentSnapshot } from "@ccv2/agent-sdk";

import { EdgeAgentBackend, LiveKitMedia, makePollSource } from "../agentSdk/edge";
import type { Contact, TelephonyEvents, TelephonyProvider, TransferKind, TransferMode } from "./types";

export interface LiveKitConfig { dialUrl: string; hangupUrl: string; agentApiBase: string; agentIdentity: string; }

export class LiveKitTelephonyProvider implements TelephonyProvider {
  readonly name = "livekit";
  private ev!: TelephonyEvents;
  private cfg: LiveKitConfig;
  private client: AgentClient | null = null;
  private media = new LiveKitMedia();
  private prev: AgentSnapshot | null = null;
  private pendingOut: Contact | null = null;

  constructor(cfg: LiveKitConfig) { this.cfg = cfg; }

  async init(events: TelephonyEvents): Promise<void> {
    this.ev = events;
    const base = this.cfg.agentApiBase;                    // /api/edge/agent/api
    const edgeBase = base.replace(/\/agent\/api\/?$/, ""); // /api/edge
    const backend = new EdgeAgentBackend(base, edgeBase, this.cfg.agentIdentity);
    this.client = new AgentClient({
      backend, media: this.media,
      transport: (emit) => new PollingTransport(makePollSource(base, this.cfg.agentIdentity), emit),
    });
    this.client.subscribe((s) => this.onSnapshot(s));
    this.client.start();   // 15s presence heartbeat (keeps the ACD agent alive)
  }

  private onSnapshot(s: AgentSnapshot): void {
    const prev = this.prev; this.prev = s;
    const id = s.interaction?.interaction_id ?? "";
    if (prev?.phase === s.phase) return;

    if (s.phase === "dialing" && id) {
      const c = this.pendingOut ?? { number: s.interaction?.ani ?? "unknown" };
      this.pendingOut = null;
      this.ev.onOutgoing(c, id);
    } else if (s.phase === "ringing" && id) {
      this.ev.onIncoming({ number: s.interaction?.ani || "unknown", name: s.interaction?.caller_name }, id);
    } else if (s.phase === "connected") {
      if (id) this.ev.onConnected(id);
    } else if (s.phase === "wrap") {
      const wid = prev?.interaction?.interaction_id ?? id;
      if (wid) this.ev.onEnded(wid, s.wrapReason === "caller_abandoned_ringing" ? "missed" : "completed");
      void this.client?.completeWrap("Handled", "");   // → idle + records wrap-up
    } else if (s.phase === "idle" && prev && (prev.phase === "ringing" || prev.phase === "dialing")) {
      const eid = prev.interaction?.interaction_id;
      if (eid) this.ev.onEnded(eid, "missed");   // rerouted / failed origination
    }
  }

  setAvailable(on: boolean): void { this.client?.setAvailability(on ? "AVAILABLE" : "NOT_READY"); }

  async dial(contact: Contact): Promise<void> {
    this.pendingOut = contact;
    await this.client?.dial(contact.number);
    // If the origination failed, the SDK stays idle (no dial_started) → surface it.
    if (this.client && this.client.getSnapshot().phase === "idle") {
      this.pendingOut = null;
      this.ev.onError("Couldn't place the call.");
    }
  }

  async answer(_callId: string): Promise<void> { await this.client?.accept(); }
  async hangup(_callId: string): Promise<void> {
    // On a ringing inbound offer, "Decline" means reject the offer (RONA →
    // requeue to another agent); once dialing/connected/hold it's a real hangup.
    if (this.client?.getSnapshot().phase === "ringing") await this.client.reject();
    else await this.client?.hangup();
  }
  async hold(_callId: string, on: boolean): Promise<void> { if (on) await this.client?.hold(); else await this.client?.resume(); }
  async mute(_callId: string, on: boolean): Promise<void> { await this.client?.setMuted(on); }
  async sendDigit(_callId: string, digit: string): Promise<void> { this.client?.sendDtmf(digit); }
  async setRecording(_callId: string, _on: boolean): Promise<void> { await this.client?.toggleRecording(); }
  async transfer(_callId: string, target: string, kind: TransferKind, mode: TransferMode): Promise<void> {
    await this.client?.transfer(target, kind, mode);
  }
  async conference(_callId: string, participant: string): Promise<void> { await this.client?.conference(participant); }
  async setAudioDevices(): Promise<void> { await this.media.applyDevices(); }

  dispose(): void { this.client?.stop(); this.client = null; }
}
